import React, {
  useState,
  useEffect,
  ReactNode,
  FC,
  createContext,
  Children,
  isValidElement,
  cloneElement,
  useContext,
  useImperativeHandle,
  Ref,
} from 'react';
import { TabContent, TabPane, Nav, NavItem, NavLink } from 'reactstrap';
import classnames from 'classnames';

import './style.css';
import { FaPlus, FaTimes } from 'react-icons/fa';

export interface ITab {
  name: ReactNode;
  content: ReactNode;

  hidden?: boolean;
  dataTestId?: string;
  onFirstOpen?: () => void;
}

export interface UncontrolledTabsRef {
  activeTab: number;
  setActiveTab: (tab: number) => void;
}

export const UncontrolledTabsContext = createContext({
  activeTab: 0,
});

type UncontrolledTabsProps = {
  id?: string,
  tabsRef?: Ref<UncontrolledTabsRef>;
  onAdd?: () => any;
  onDelete?: (index: number) => any;
  className?: string;
};

export const UncontrolledTabs: FC<UncontrolledTabsProps> = ({ id, tabsRef, children, onAdd, onDelete, className = "flex-container" }) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const noChildren = Children.count(children);
  useImperativeHandle(
    tabsRef,
    () => ({
      activeTab,
      setActiveTab,
    }),
    [activeTab, setActiveTab],
  );

  useEffect(() => {
    if (activeTab >= noChildren) {
      setActiveTab(Math.max(0, noChildren - 1));
    }
  }, [setActiveTab, activeTab, noChildren]);

  return (
    <UncontrolledTabsContext.Provider value={{ activeTab }}>
      <div className={className} data-tabs-id={id}>
        <div className="tab-scroll">
          <Nav tabs>
            {Children.map(children, (child, index) => {
              if (!isValidElement(child)) {
                return null;
              }
              if (child.props.hidden) {
                return null;
              }
              return (
                <NavItem key={index} style={{ order: index + 1 }}>
                  <NavLink
                    className={classnames({
                      active: activeTab === index,
                    })}
                    onClick={() => {
                      setActiveTab(index);
                    }}
                    data-tab-id-toggle={index}
                    data-testid={`tab-title-${child.props.dataTestId || index}`}
                  >
                    {child.props.name}
                    {onDelete && <FaTimes className="small-icon" onClick={() => onDelete(index)} />}
                  </NavLink>
                </NavItem>
              );
            })}
            {onAdd && (
              <NavItem style={{ order: Children.count(children) + 2 }}>
                <NavLink
                  onClick={async () => {
                    if (await onAdd()) {
                      setActiveTab(noChildren);
                    }
                  }}
                >
                  <FaPlus className="small-icon" />
                </NavLink>
              </NavItem>
            )}
          </Nav>
        </div>

        <TabContent activeTab={activeTab} className="flex-fill-height">
          {Children.map(children, (child, index) => {
            if (!isValidElement(child)) {
              return null;
            }
            if (child.props.hidden) {
              return null;
            }
            return (
              <TabPane
                key={index}
                tabId={index}
                data-tab-id-content={index}
                data-testid={`tab-content-${child.props.dataTestId || index}`}
                style={{ height: '100%' }}
              >
                <div className="flex-container">{cloneElement(child, { index } as any)}</div>
              </TabPane>
            );
          })}
        </TabContent>
      </div>
    </UncontrolledTabsContext.Provider>
  );
};

export const Tab: FC<{
  name: ReactNode;
  hidden?: boolean;
  dataTestId?: string;
  onFirstOpen?: () => void;
  index?: number;
}> = ({ children, index, onFirstOpen }) => {
  const ctx = useContext(UncontrolledTabsContext);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  useEffect(() => {
    if (index === ctx.activeTab && !hasBeenOpened) {
      onFirstOpen?.();
      setHasBeenOpened(true);
    }
  }, [ctx.activeTab, index, onFirstOpen, hasBeenOpened]);
  return <>{children}</>;
};
